import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { type ReactNode } from 'react'
import { queryClient } from './lib/queryClient'
import { useAuthStore } from './stores/authStore'
import type { Ruolo } from './lib/tipi'

import { AppShell } from './layout/AppShell'
import { ToastContainer } from './components/ui/Toast'
import { Accedi } from './pages/auth/Accedi'
import { Registrati } from './pages/auth/Registrati'
import { PasswordDimenticata } from './pages/auth/PasswordDimenticata'
import { ResetPassword } from './pages/auth/ResetPassword'
import { ConfermaEmail } from './pages/auth/ConfermaEmail'

import { SdrDashboard } from './pages/sdr/Dashboard'
import { SdrLeads } from './pages/sdr/Leads'
import { MieiLead } from './pages/sdr/MieiLead'
import { SdrSessioni } from './pages/sdr/Sessioni'
import { SessioneDettaglio } from './pages/sdr/SessioneDettaglio'
import { SdrProfilo } from './pages/sdr/Profilo'
import { Abbonamento } from './pages/sdr/Abbonamento'

import { ManagerDashboard } from './pages/manager/Dashboard'
import { ManagerTeam } from './pages/manager/Team'
import { ManagerSessioni } from './pages/manager/Sessioni'
import { ManagerLeads } from './pages/manager/Leads'
import { ManagerReport } from './pages/manager/Report'

import { AdminDashboard } from './pages/superadmin/Dashboard'
import { AdminUtenti } from './pages/superadmin/Utenti'
import { AdminUtenteDettaglio } from './pages/superadmin/UtenteDettaglio'
import { AdminTeams } from './pages/superadmin/Teams'
import { AdminTeamDettaglio } from './pages/superadmin/TeamDettaglio'
import { AdminLeads } from './pages/superadmin/Leads'
import { AdminRevenue } from './pages/superadmin/Revenue'
import { AdminCostiAI } from './pages/superadmin/CostiAI'
import { AdminAuditLog } from './pages/superadmin/AuditLog'

interface RequireRuoloProps {
  ruoli: Ruolo[]
  children: ReactNode
}

/**
 * Guard di routing: blocca l'accesso se l'utente non è autenticato
 * o non ha uno dei ruoli richiesti.
 */
function RequireRuolo({ ruoli, children }: RequireRuoloProps) {
  const { token, utente } = useAuthStore()
  const location = useLocation()

  if (!token || !utente) {
    return <Navigate to="/accedi" state={{ from: location }} replace />
  }
  if (!ruoli.includes(utente.ruolo)) {
    return <Navigate to={homePerRuolo(utente.ruolo)} replace />
  }
  return <>{children}</>
}

function homePerRuolo(r: Ruolo): string {
  if (r === 'superadmin') return '/admin/dashboard'
  if (r === 'manager') return '/manager/dashboard'
  return '/app/dashboard'
}

function RedirectPerRuolo() {
  const { utente } = useAuthStore()
  if (!utente) return <Navigate to="/accedi" replace />
  return <Navigate to={homePerRuolo(utente.ruolo)} replace />
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastContainer />
      <BrowserRouter>
        <Routes>
          {/* Pubbliche */}
          <Route path="/accedi" element={<Accedi />} />
          <Route path="/registrati" element={<Registrati />} />
          {/* Alias: il link di invito team del backend usa /registra */}
          <Route path="/registra" element={<Registrati />} />
          {/* Reset password: il percorso canonico è /auth/* per i link
              email Supabase. Gli alias senza prefisso /auth restano
              attivi per retrocompatibilità con vecchie configurazioni. */}
          <Route path="/auth/password-dimenticata" element={<PasswordDimenticata />} />
          <Route path="/password-dimenticata" element={<PasswordDimenticata />} />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Callback Supabase Auth dopo click sul link di verifica email.
              `/callback` è l'alias usato dai redirect Supabase quando il
              Site URL è configurato senza prefisso `/auth/`. */}
          <Route path="/auth/callback" element={<ConfermaEmail />} />
          <Route path="/callback" element={<ConfermaEmail />} />

          {/* SDR (e ruoli superiori che possono guardare la stessa UI) */}
          <Route
            element={
              <RequireRuolo ruoli={['sdr', 'manager', 'superadmin']}>
                <AppShell />
              </RequireRuolo>
            }
          >
            <Route path="/app/dashboard"      element={<SdrDashboard />} />
            <Route path="/app/leads"          element={<SdrLeads />} />
            <Route path="/app/miei-lead"      element={<MieiLead />} />
            <Route path="/app/sessioni"       element={<SdrSessioni />} />
            <Route path="/app/sessioni/:id"   element={<SessioneDettaglio />} />
            <Route path="/app/profilo"        element={<SdrProfilo />} />
            <Route path="/app/abbonamento"    element={<Abbonamento />} />
          </Route>

          {/* Manager */}
          <Route
            element={
              <RequireRuolo ruoli={['manager', 'superadmin']}>
                <AppShell />
              </RequireRuolo>
            }
          >
            <Route path="/manager/dashboard"  element={<ManagerDashboard />} />
            <Route path="/manager/team"       element={<ManagerTeam />} />
            <Route path="/manager/sessioni"   element={<ManagerSessioni />} />
            <Route path="/manager/leads"      element={<ManagerLeads />} />
            <Route path="/manager/report"     element={<ManagerReport />} />
          </Route>

          {/* Superadmin */}
          <Route
            element={
              <RequireRuolo ruoli={['superadmin']}>
                <AppShell />
              </RequireRuolo>
            }
          >
            <Route path="/admin/dashboard"      element={<AdminDashboard />} />
            <Route path="/admin/utenti"         element={<AdminUtenti />} />
            <Route path="/admin/utenti/:id"     element={<AdminUtenteDettaglio />} />
            <Route path="/admin/teams"          element={<AdminTeams />} />
            <Route path="/admin/teams/:id"      element={<AdminTeamDettaglio />} />
            <Route path="/admin/leads"          element={<AdminLeads />} />
            <Route path="/admin/revenue"        element={<AdminRevenue />} />
            <Route path="/admin/costi-ai"       element={<AdminCostiAI />} />
            <Route path="/admin/audit-log"      element={<AdminAuditLog />} />
          </Route>

          {/* Root e catchall */}
          <Route path="/" element={<RedirectPerRuolo />} />
          <Route path="*" element={<RedirectPerRuolo />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
